import { useEffect, useState } from 'react'

/**
 * Instalar la app desde dentro de la app.
 *
 * Hasta ahora la pantalla de sistema decía «en Chrome: menú, Añadir a pantalla de
 * inicio», y eso es mandar a alguien a buscar por un menú que cambia con cada
 * versión del navegador y con cada fabricante. Peor: si el navegador no puede
 * instalarla, quien lo intenta se encuentra un mensaje del sistema que no explica
 * nada, y no hay manera de saber si el problema es la app o el navegador.
 *
 * El navegador avisa de que puede instalarla con `beforeinstallprompt`, y ese
 * aviso llega **una sola vez y muy pronto**, normalmente antes de que React haya
 * montado nada. Por eso se escucha al cargar el módulo y se guarda: un `useEffect`
 * llegaría tarde y el botón no aparecería nunca.
 *
 * Y si no llega, la app lo dice en vez de callarse. Que el navegador no lo ofrezca
 * es una respuesta: en iPhone y iPad nunca lo ofrece —allí se hace desde el menú
 * de compartir— y en un Android sin los servicios de Google tampoco.
 */

/** Lo que Chrome pasa en el evento, que no está en los tipos del DOM. */
interface PromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice?: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let guardado: PromptEvent | null = null
const avisos = new Set<() => void>()

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Sin esto el navegador enseña su propia franja, que en una tablet de un niño
    // es un botón más en el que tocar sin saber qué hace.
    event.preventDefault()
    guardado = event as PromptEvent
    for (const avisar of avisos) avisar()
  })

  window.addEventListener('appinstalled', () => {
    guardado = null
    for (const avisar of avisos) avisar()
  })
}

/** Solo para las pruebas: deja el módulo como estaba. */
export function olvidarInstalacionParaPruebas(): void {
  guardado = null
}

export interface Instalacion {
  /** Si el navegador ha dicho que puede instalarla. */
  readonly puede: boolean
  /** Lanza el diálogo del navegador. Devuelve si aceptó. */
  readonly instalar: () => Promise<boolean>
}

export function useInstalacion(): Instalacion {
  const [puede, setPuede] = useState(guardado !== null)

  useEffect(() => {
    const avisar = (): void => {
      setPuede(guardado !== null)
    }
    avisos.add(avisar)
    // El evento pudo llegar entre el primer render y este efecto.
    avisar()
    return () => {
      avisos.delete(avisar)
    }
  }, [])

  return {
    puede,
    instalar: async () => {
      const evento = guardado
      if (!evento) return false
      await evento.prompt()
      const eleccion = await evento.userChoice
      // El aviso sirve una sola vez: una vez usado, el navegador no lo repite.
      guardado = null
      for (const avisar of avisos) avisar()
      return eleccion?.outcome === 'accepted'
    },
  }
}
